import React, { createContext, useState, useContext, useRef } from 'react';
import { questions } from '../data/questions';
import { supabase } from '../lib/supabaseClient';

const QuizContext = createContext();

export const useQuiz = () => useContext(QuizContext);

export const QuizProvider = ({ children }) => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  const hasSubmittedRef = useRef(false);
  const SESSION_SUBMITTED_KEY = 'quiz_submitted';
  const SUBMISSION_ID_KEY = 'quiz_submission_id';

  const getSubmissionId = () => {
    if (typeof window === 'undefined') return null;
    let id = sessionStorage.getItem(SUBMISSION_ID_KEY);
    if (!id) {
      if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
        id = window.crypto.randomUUID();
      } else {
        id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
      sessionStorage.setItem(SUBMISSION_ID_KEY, id);
    }
    return id;
  };

  // Calcula o progresso atual do quiz
  const calculateProgress = () => {
    const totalQuestions = questions.reduce((acc, phase) => acc + phase.questions.length, 0);
    const answeredQuestions = Object.keys(answers).length;
    return (answeredQuestions / totalQuestions) * 100;
  };

  // Avança para a próxima pergunta ou fase
  const nextQuestion = () => {
    const currentPhaseQuestions = questions.find(phase => phase.id === currentPhase).questions;
    
    if (currentQuestionIndex < currentPhaseQuestions.length - 1) {
      // Avança para a próxima pergunta na fase atual
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Verifica se há uma próxima fase
      const nextPhaseIndex = questions.findIndex(phase => phase.id === currentPhase) + 1;
      
      if (nextPhaseIndex < questions.length) {
        // Avança para a próxima fase
        setCurrentPhase(questions[nextPhaseIndex].id);
        setCurrentQuestionIndex(0);
      } else {
        // Finaliza o quiz e gera o diagnóstico
        generateDiagnosis();
      }
    }
  };

  // Salva a resposta do usuário
  const saveAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Gera o diagnóstico com base nas respostas
  const generateDiagnosis = () => {
    if (loading || diagnosis || hasSubmittedRef.current) {
      return;
    }
    if (typeof window !== 'undefined' && sessionStorage.getItem(SESSION_SUBMITTED_KEY) === '1') {
      return;
    }
    hasSubmittedRef.current = true;
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_SUBMITTED_KEY, '1');
    }
    setLoading(true);

    setTimeout(async () => {
      let mainIssue = '';
      
      if (answers['mix_accounts'] === 'Sim, quase sempre' || answers['mix_accounts'] === 'Às vezes, sem querer') {
        mainIssue = 'falta de separação de contas pessoais e empresariais';
      } 
      else if (answers['cash_knowledge'] === 'Não faço ideia' || answers['cash_knowledge'] === 'Mais ou menos, olhando o extrato') {
        mainIssue = 'falta de clareza sobre a situação real do caixa';
      }
      else if (answers['financial_control'] === 'Só pelo extrato bancário' || answers['financial_control'] === 'No papel/caderno') {
        mainIssue = 'ausência de um sistema adequado de controle financeiro';
      }
      else if (answers['common_issues'] === 'Dinheiro entra, mas some sem saber para onde') {
        mainIssue = 'falta de rastreamento efetivo das despesas';
      }
      else {
        mainIssue = 'ausência de controle real do caixa';
      }

      const submissionId = getSubmissionId();

      const row = {
        profile: answers['profile'] || null,
        name: answers['name'] || null,
        email: answers['email'] || null,
        phone: answers['phone'] || null,
        company_size: answers['company_size'] || null,
        mix_accounts: answers['mix_accounts'] || null,
        financial_control: answers['financial_control'] || null,
        common_issues: answers['common_issues'] || null,
        cash_knowledge: answers['cash_knowledge'] || null,
        want_solution: answers['want_solution'] || null,
        next_step: answers['next_step'] || null,
        separate_accounts: answers['separate_accounts'] || null,
        simple_tool: answers['simple_tool'] || null,
        start_today: answers['start_today'] || null,
        diagnosis: mainIssue,
        answers: answers,
        submission_id: submissionId || null
      };

      try {
        const { error } = await supabase
          .from('quiz_responses')
          .upsert([row], { onConflict: 'submission_id', ignoreDuplicates: true });
        if (error) {
          console.error('supabase insert error', error);
          hasSubmittedRef.current = false;
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem(SESSION_SUBMITTED_KEY);
          }
        }
      } catch (e) {
        console.error('supabase insert exception', e);
        hasSubmittedRef.current = false;
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem(SESSION_SUBMITTED_KEY);
        }
      }

      setDiagnosis(mainIssue);
      setLoading(false);
    }, 3000);
  };
  
  const value = {
    currentPhase,
    currentQuestionIndex,
    answers,
    loading,
    diagnosis,
    calculateProgress,
    nextQuestion,
    saveAnswer,
    generateDiagnosis
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};
